import React, { useState } from "react"
import { Link } from "react-router-dom"
import IconMinusCircle from "../../../assets/icons/icon-minus-circle.svg"
import IconInfo from "../../../assets/icons/icon-info-blue.svg"
import { resolveUrlForArticles } from "./utils/urlUtils"
import { interpolate, getCurrentUnixTimestamp } from "./utils/displayUtils"
import { ADDITIONAL_REQUIREMENT_KEY } from "./utils/additionalRequirementItems"
import { EVENTS_URLS } from "./utils/constants/externalUrls"
import {
  AdditionalRequirementsSection,
  AdditionalRequirementsHeader,
  AdditionalRequirementsTitle,
  AdditionalRequirementsIcon,
  AdditionalRequirementsTitleText,
  AdditionalRequirementsCount,
  AdditionalRequirementsBody,
  AdditionalRequirementsDescription,
  AdditionalRequirementCard,
  AdditionalRequirementHeader,
  AdditionalRequirementIcon,
  AdditionalRequirementCompleteIcon,
  AdditionalRequirementCheckmark,
  AdditionalRequirementTitle,
  AdditionalRequirementDescription,
  AdditionalRequirementContent,
  AdditionalRequirementActions,
  AdditionalRequirementTextLink,
  ActionButton,
  ActionButtonText,
} from "./Styles/ProgressTracker.styles"

const RequirementRowIcon = ({ item }) => {
  if (item.complete) {
    return (
      <AdditionalRequirementCompleteIcon>
        <AdditionalRequirementCheckmark />
      </AdditionalRequirementCompleteIcon>
    )
  }

  return (
    <AdditionalRequirementIcon
      src={item.hasWarning ? IconMinusCircle : IconInfo}
      alt=""
    />
  )
}

const ChecklistItem = ({ item, unixTimestamp }) => {
  const primaryHref =
    item.primaryLinkKind === "pat-search"
      ? interpolate(EVENTS_URLS.PAT_SEARCH, { unixTimestamp })
      : item.primaryLinkUrl
  const secondaryHref = item.secondaryLinkUrl
    ? resolveUrlForArticles(item.secondaryLinkUrl)
    : null
  const showActions = !item.complete && (primaryHref || secondaryHref)

  return (
    <AdditionalRequirementCard $hasWarning={item.hasWarning && !item.complete}>
      <AdditionalRequirementHeader>
        <RequirementRowIcon item={item} />
        <AdditionalRequirementTitle>{item.title}</AdditionalRequirementTitle>
      </AdditionalRequirementHeader>
      <AdditionalRequirementContent>
        <AdditionalRequirementDescription>
          {item.description}
        </AdditionalRequirementDescription>
        {showActions && (
          <AdditionalRequirementActions>
            {item.primaryLinkText &&
              primaryHref &&
              (item.primaryLinkKind === "in-app" ? (
                <ActionButton as={Link} to={primaryHref}>
                  <ActionButtonText>{item.primaryLinkText}</ActionButtonText>
                </ActionButton>
              ) : (
                <ActionButton
                  as="a"
                  href={primaryHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ActionButtonText>{item.primaryLinkText}</ActionButtonText>
                </ActionButton>
              ))}
            {item.secondaryLinkText && secondaryHref && (
              <AdditionalRequirementTextLink
                as="a"
                href={secondaryHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.secondaryLinkText}
              </AdditionalRequirementTextLink>
            )}
          </AdditionalRequirementActions>
        )}
      </AdditionalRequirementContent>
    </AdditionalRequirementCard>
  )
}

const completionSummary = items => {
  const checklistItems = items.filter(
    item => item.countsTowardCompletion !== false,
  )

  if (checklistItems.length === 0) {
    return null
  }

  const completeCount = checklistItems.filter(item => item.complete).length

  return {
    completeCount,
    totalCount: checklistItems.length,
  }
}

export const getAdditionalRequirementsCompletionSummary = completionSummary

export const formatAdditionalRequirementsButtonLabel = summary => {
  if (!summary) {
    return "ADDITIONAL REQUIREMENTS"
  }

  return `ADDITIONAL REQUIREMENTS (${summary.completeCount}/${summary.totalCount})`
}

const sortAdditionalRequirementItems = items => {
  const order = [
    ADDITIONAL_REQUIREMENT_KEY.PAT,
    ADDITIONAL_REQUIREMENT_KEY.BACKGROUND_CHECK,
    ADDITIONAL_REQUIREMENT_KEY.EXPERIENCE_CREDITS,
    ADDITIONAL_REQUIREMENT_KEY.EMPLOYMENT,
    ADDITIONAL_REQUIREMENT_KEY.AGE,
    ADDITIONAL_REQUIREMENT_KEY.GOOD_STANDING,
    ADDITIONAL_REQUIREMENT_KEY.ACCEPTABLE_PROGRESS,
  ]
  const orderIndex = key => {
    const index = order.indexOf(key)
    return index === -1 ? Number.MAX_SAFE_INTEGER : index
  }
  return [...items].sort((a, b) => orderIndex(a.key) - orderIndex(b.key))
}

const AdditionalRequirementsList = ({
  items = [],
  defaultExpanded,
  collapsible,
  showSectionHeader = true,
}) => {
  const isCollapsible = collapsible ?? true
  const [isExpanded, setIsExpanded] = useState(() => {
    if (!isCollapsible) {
      return true
    }

    return defaultExpanded ?? true
  })
  const unixTimestamp = getCurrentUnixTimestamp()
  const showBody = !isCollapsible || isExpanded

  if (items.length === 0) {
    return null
  }

  const orderedItems = sortAdditionalRequirementItems(items)
  const summary = completionSummary(orderedItems)

  const listBody = (
    <AdditionalRequirementsBody>
      <AdditionalRequirementsDescription>
        Additional requirements do not need to be completed in a specific level
        but must be completed before applying for PGA of America Membership.
      </AdditionalRequirementsDescription>

      {orderedItems.map(item => (
        <ChecklistItem
          key={item.key}
          item={item}
          unixTimestamp={unixTimestamp}
        />
      ))}
    </AdditionalRequirementsBody>
  )

  if (!showSectionHeader) {
    return listBody
  }

  return (
    <AdditionalRequirementsSection>
      <AdditionalRequirementsHeader
        $collapsible={isCollapsible}
        onClick={
          isCollapsible ? () => setIsExpanded(current => !current) : undefined
        }
      >
        <AdditionalRequirementsTitle>
          {isCollapsible && (
            <AdditionalRequirementsIcon
              style={{
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
          )}
          <AdditionalRequirementsTitleText>
            Additional Requirements
          </AdditionalRequirementsTitleText>
        </AdditionalRequirementsTitle>
        {summary && (
          <AdditionalRequirementsCount>
            {summary.completeCount} of {summary.totalCount} complete
          </AdditionalRequirementsCount>
        )}
      </AdditionalRequirementsHeader>

      {showBody && listBody}
    </AdditionalRequirementsSection>
  )
}

export default AdditionalRequirementsList
